import { ERROR_CODES } from '@garage/shared';
import { useSyncExternalStore } from 'react';

/**
 * Incidents de session signalés par l'API sur n'importe quelle requête (vague 5) :
 * - `garage_suspended` : le garage actif est suspendu → écran dédié avec le message du serveur ;
 * - `support_access_ended` : la session support n'est plus valable → retour à la console.
 * Le client d'API les publie ici ; l'espace garage les affiche. Aucune donnée personnelle.
 */
export type SessionIssueCode =
  | typeof ERROR_CODES.garageSuspended
  | typeof ERROR_CODES.supportAccessEnded
  /** S35 : la session est passée sur un autre garage dans un autre onglet (409). */
  | typeof ERROR_CODES.garageMismatch;
export interface SessionIssue {
  code: SessionIssueCode;
  message: string;
}

const WATCHED = new Set<string>([ERROR_CODES.garageSuspended, ERROR_CODES.supportAccessEnded, ERROR_CODES.garageMismatch]);

let current: SessionIssue | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

/** Appelé par le client d'API pour toute erreur : ne retient que les codes de session surveillés. */
export function reportApiIssue(code: string, message: string): void {
  if (!WATCHED.has(code)) return;
  if (current?.code === code && current.message === message) return;
  current = { code: code as SessionIssueCode, message };
  emit();
}

export function clearSessionIssue(): void {
  if (!current) return;
  current = null;
  emit();
}

export function getSessionIssue(): SessionIssue | null {
  return current;
}

export function useSessionIssue(): SessionIssue | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
    () => null,
  );
}

// ─── Accès refusé (401/403) dans l'espace garage ────────────────────────────
// Un refus peut être un simple droit manquant, ou la fin de l'accès lui-même (membre retiré, accès
// support terminé, y compris sous l'ancien code `forbidden`). L'espace garage relit alors la session
// (`auth.me`) et compare : c'est la session, pas le code d'erreur, qui fait foi.
const deniedListeners = new Set<(code: string) => void>();

export function reportAccessDenied(code: string): void {
  for (const l of deniedListeners) l(code);
}

export function onAccessDenied(listener: (code: string) => void): () => void {
  deniedListeners.add(listener);
  return () => {
    deniedListeners.delete(listener);
  };
}
